import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_WHS_DAILY_TASK_MONITORING_LIST,
  DailyTaskMonitoringSummaryType,
  DailyTaskMonitoringType,
  DailyTaskMonitoringPageRequestType,
  DailyTaskMonitoringListData,
  GetWhsDailyTaskMonitoringListResponse
} from '../queries/getDailyTaskMonitoringList';
import { GET_DSP_DAILY_TASK_MONITORING_LIST, GetDspDailyTaskMonitoringListResponse } from '../queries/getDspDailyTaskMonitoringList';

export type DailyTaskMonitoringMode = 'dsp' | 'whs';

export const useDailyTaskMonitoringQuery = (mode: DailyTaskMonitoringMode): {
  summaryData: DailyTaskMonitoringSummaryType | null;
  recordData: DailyTaskMonitoringType[];
  calculatedAt: string | null;
  loading: boolean;
  error: any;
  refetch: any;
  handleFilter: (pageRequest: DailyTaskMonitoringPageRequestType) => void;
  clearData: () => void;
  currentFilters: DailyTaskMonitoringPageRequestType | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
} => {
  const [pageRequest, setPageRequest] = useState<DailyTaskMonitoringPageRequestType | null>(null);
  const [shouldQuery, setShouldQuery] = useState(false);
  const [queryData, setQueryData] = useState<GetWhsDailyTaskMonitoringListResponse | GetDspDailyTaskMonitoringListResponse | null>(null);

  const isDsp = mode === 'dsp';

  const [executeQuery, { data, loading, error, refetch }] = useLazyQuery<GetWhsDailyTaskMonitoringListResponse | GetDspDailyTaskMonitoringListResponse>(
    isDsp ? GET_DSP_DAILY_TASK_MONITORING_LIST : GET_WHS_DAILY_TASK_MONITORING_LIST,
    {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only', // 每次查询都从网络获取，不使用缓存，避免数据累积
    }
  );

  // 当查询结果更新时，更新本地状态
  useEffect(() => {
    // 无论 data 是否为 null，都更新 queryData
    // 这样可以确保数据状态与查询结果保持一致
    setQueryData(data || null);
  }, [data, loading, error]);

  // 当pageRequest变化时执行查询
  useEffect(() => {
    if (pageRequest && shouldQuery) {
      // 清理 filter 对象，移除 undefined 值，但保留有值的字段
      const cleanFilter = Object.fromEntries(
        Object.entries(pageRequest.filter).filter(([_, value]) => value !== undefined)
      ) as typeof pageRequest.filter;
      
      // 不清空数据，保留旧数据直到新数据到达，避免显示 "No data"
      // 只有在查询成功时才会更新 queryData
      
      const variables: Record<string, unknown> = {
        filter: cleanFilter,
        pageRequest: pageRequest.pageRequest
      };
      // WHS 版本 query 需要顶层 warehouseId（必传，aspect 用它做仓库权限校验）；DSP 版本签名未变
      if (!isDsp) {
        variables.warehouseId = pageRequest.filter.warehouseId;
      }
      executeQuery({ variables });
      setShouldQuery(false);
    }
  }, [pageRequest, shouldQuery, executeQuery, queryData, isDsp]);

  const handleFilter = (newPageRequest: DailyTaskMonitoringPageRequestType) => {
    // 如果 pageRequest 为 null，说明数据已被清空（比如切换维度），需要清空数据
    // 或者检查是否是切换维度（通过比较 filter 中的关键字段）
    const isViewModeChanged = !pageRequest || (
      pageRequest.filter.warehouseId !== newPageRequest.filter.warehouseId ||
      JSON.stringify(pageRequest.filter) !== JSON.stringify(newPageRequest.filter)
    );
    
    // 如果切换了维度或查询参数发生重大变化，清空数据
    if (isViewModeChanged) {
      setQueryData(null);
    }
    
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  // 清空数据的方法
  const clearData = () => {
    setQueryData(null);
    // 清空 pageRequest，这样 handleFilter 能正确检测到维度切换
    setPageRequest(null);
    // 不清空 shouldQuery，因为后续的 handleFilter 会重新设置它
  };

  // 使用本地状态而不是直接使用 data，避免数据累积
  const taskList: DailyTaskMonitoringListData | undefined = isDsp
    ? (queryData as GetDspDailyTaskMonitoringListResponse | null)?.getDailyTaskMonitoringListForDsp
    : (queryData as GetWhsDailyTaskMonitoringListResponse | null)?.getWhsDailyTaskMonitoringList;
  const driverSummaryData = taskList?.summaryData || null;
  const recordData = taskList?.recordData || [];
  const calculatedAt = taskList?.calculatedAt || null;


  return {
    summaryData: driverSummaryData,
    recordData,
    calculatedAt,
    loading,
    error,
    refetch,
    handleFilter,
    clearData,
    currentFilters: pageRequest,
    pagination: taskList ? {
      page: taskList.page,
      size: taskList.size,
      totalElements: taskList.totalElements,
      totalPages: taskList.totalPages,
      hasNext: taskList.hasNext,
      hasPrevious: taskList.hasPrevious
    } : null
  };
};
